/**
 * Notification sound utilities using Web Audio API.
 * No audio files needed — all sounds are synthesized.
 */

/**
 * Play a simple 800Hz beep for generic notifications (WhatsApp replies, dispositions, etc.)
 * Extracted from NotificationContext.tsx
 */
export function playGenericNotificationBeep(): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.1;

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch {
    // Silently fail if audio is not available
    console.debug('Notification sound not available');
  }
}

/**
 * Play an ascending C-E-G major chord arpeggio for new lead assignment notifications.
 * Three sine wave oscillators with staggered starts and gain envelopes (~0.8s total).
 */
export function playLeadNotificationChime(): void {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const now = audioContext.currentTime;

    // C5 (523Hz), E5 (659Hz), G5 (784Hz) — ascending major chord
    const notes = [
      { freq: 523.25, start: 0 },
      { freq: 659.25, start: 0.15 },
      { freq: 783.99, start: 0.3 },
    ];

    notes.forEach(({ freq, start }) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();

      osc.connect(gain);
      gain.connect(audioContext.destination);

      osc.frequency.value = freq;
      osc.type = 'sine';

      // Envelope: quick attack, sustain, gentle decay
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.12, now + start + 0.04);
      gain.gain.linearRampToValueAtTime(0.08, now + start + 0.2);
      gain.gain.linearRampToValueAtTime(0, now + start + 0.5);

      osc.start(now + start);
      osc.stop(now + start + 0.5);
    });
  } catch {
    // Silently fail if audio is not available
    console.debug('Lead notification chime not available');
  }
}
